/**
 * Lower-cases a label's first letter for use mid-sentence, e.g. "TTV (BR) by product",
 * "Which product drove the TTV (BR) change?" (review finding: REVIEW_LOG.md, a live screenshot
 * showed capitalised split labels reading oddly mid-sentence, and a metric label wrongly
 * lower-cased alongside them). A metric label (e.g. "TTV (BR)") is never passed through this
 * helper: it is always rendered verbatim, per the same review finding.
 *
 * The exception is a label that starts with an acronym (two or more consecutive capital letters,
 * e.g. a metric-style label) or a proper noun, neither of which reads correctly lower-cased.
 * Proper-noun detection isn't feasible generically from a bare string, so only the acronym case
 * is implemented; none of the eight breakdown split labels this app has today (Product, Market,
 * Sale channel, Platform, New vs repeat, Domestic vs international, Customer country, Destination
 * region) are proper nouns, so in practice every one of them gets its first letter lowered.
 */
export function midSentenceLabel(label: string): string {
  if (!label) {
    return label;
  }
  const firstWord = label.match(/^\S+/)?.[0] ?? '';
  const startsWithAcronym = /^[A-Z]{2,}/.test(firstWord);
  if (startsWithAcronym) {
    return label;
  }
  return label.charAt(0).toLowerCase() + label.slice(1);
}

const TITLE_CASE_MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'nor',
  'of',
  'on',
  'or',
  'per',
  'the',
  'to',
  'via',
  'vs',
  'with',
]);

/**
 * Formats a governed label for use as an Analytics title without changing the source contract.
 * Acronyms and branded mixed-case words are preserved, while ordinary words follow title case.
 */
export function analyticsTitle(label: string): string {
  const words = label.trim().split(/\s+/);
  if (!label.trim()) {
    return label;
  }

  return words
    .map((word, index) => {
      const bareWord = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9+%]+$/g, '');
      const lowerWord = bareWord.toLowerCase();
      const isEdge = index === 0 || index === words.length - 1;
      const isAcronym = /^[A-Z0-9]{2,}(?:\+|%)?$/.test(bareWord.replace(/[()]/g, ''));
      const isBrandedCase = /[a-z][A-Z]|[A-Z][a-z]+[A-Z]/.test(bareWord);

      if (isAcronym || isBrandedCase || (!isEdge && TITLE_CASE_MINOR_WORDS.has(lowerWord))) {
        return word;
      }

      const firstLetter = word.search(/[A-Za-z]/);
      if (firstLetter < 0) {
        return word;
      }
      return `${word.slice(0, firstLetter)}${word.charAt(firstLetter).toUpperCase()}${word.slice(firstLetter + 1)}`;
    })
    .join(' ');
}
